import { type InfiniteData, type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useSyncExternalStore } from "react";
import { useFilterOptionsClient } from "../../api/filterOptionsQuery.ts";

interface NamedOptionPage {
    items: { id: string; displayName?: string; name?: string }[];
}

const NO_NAMES = new Map<string, string>();

function collectCachedNames(queryClient: QueryClient, queryKey: readonly unknown[]): Map<string, string> {
    const namesById = new Map<string, string>();

    for (const [, data] of queryClient.getQueriesData<InfiniteData<NamedOptionPage>>({ queryKey })) {
        for (const page of data?.pages ?? []) {
            for (const option of page.items) {
                const name = option.displayName ?? option.name;

                if (name) {
                    namesById.set(option.id, name);
                }
            }
        }
    }

    return namesById;
}

function haveSameNames(a: Map<string, string>, b: Map<string, string>): boolean {
    return a.size === b.size && [...a].every(([id, name]) => b.get(id) === name);
}

function useCachedNames(queryKey: readonly unknown[]): Map<string, string> {
    const queryClient = useQueryClient();
    const namesRef = useRef(NO_NAMES);

    const subscribe = useCallback((onStoreChange: () => void) => queryClient.getQueryCache().subscribe(onStoreChange), [queryClient]);

    const getSnapshot = useCallback(() => {
        const next = collectCachedNames(queryClient, queryKey);

        if (!haveSameNames(namesRef.current, next)) {
            namesRef.current = next;
        }

        return namesRef.current;
    }, [queryClient, queryKey]);

    return useSyncExternalStore(subscribe, getSnapshot);
}

export function useCachedManagerNames(): Map<string, string> {
    const client = useFilterOptionsClient();
    const queryKey = useMemo(() => client.searchReportingManagers.key(), [client]);

    return useCachedNames(queryKey);
}

export function useCachedSegmentNames(): Map<string, string> {
    const client = useFilterOptionsClient();
    const queryKey = useMemo(() => client.searchSegments.key(), [client]);

    return useCachedNames(queryKey);
}
